import { differenceInHours, subHours } from "date-fns";

export interface QuoteFollowUpWakeUpTiming {
  dueAt: Date | string;
  displayMetadata?: unknown | null;
}

const FOLLOW_UP_DELAY_HOURS = 48;
const FOLLOW_UP_SEQUENCE_TOLERANCE_MS = 60 * 1000;

export const getQuoteFollowUpWakeUpTitle = (dueWakeUp: QuoteFollowUpWakeUpTiming): string => {
  const { occurredAt, isSubsequent } = getQuoteFollowUpWakeUpTiming(dueWakeUp);
  const titlePrefix = isSubsequent ? "Last followed up" : "Follow up";
  const hoursAgo = formatHoursAgo(occurredAt);

  return `${titlePrefix} ${hoursAgo} ago`;
};

export const getQuoteFollowUpWakeUpTiming = (
  dueWakeUp: QuoteFollowUpWakeUpTiming
): { occurredAt: Date; isSubsequent: boolean } => {
  const metadata = getMetadataObject(dueWakeUp.displayMetadata);
  const dueBasedReferenceDate = subHours(new Date(dueWakeUp.dueAt), FOLLOW_UP_DELAY_HOURS);
  const firstReferenceDate = getMetadataDate(metadata, ["quoteSentAt", "sentAt"]);
  const followUpReferenceDate = getMetadataDate(metadata, ["lastFollowedUpAt", "followedUpAt", "resolvedAt"]);
  const isSubsequent = isSubsequentWakeUp(metadata, dueBasedReferenceDate, firstReferenceDate);

  if (isSubsequent) {
    return {
      occurredAt: followUpReferenceDate ?? dueBasedReferenceDate,
      isSubsequent,
    };
  }

  return {
    occurredAt: firstReferenceDate ?? dueBasedReferenceDate,
    isSubsequent,
  };
};

const isSubsequentWakeUp = (
  metadata: Record<string, unknown> | null,
  dueBasedReferenceDate: Date,
  firstReferenceDate: Date | null
): boolean => {
  const sequence = getMetadataNumber(metadata, [
    "sequence",
    "wakeUpSequence",
    "quoteFollowUpSequence",
    "followUpSequence",
  ]);

  if (sequence && sequence > 1) {
    return true;
  }

  const resolvedCount = getMetadataNumber(metadata, ["resolvedCount"]);

  if (resolvedCount && resolvedCount > 0) {
    return true;
  }

  if (!firstReferenceDate) {
    return false;
  }

  return dueBasedReferenceDate.getTime() - firstReferenceDate.getTime() > FOLLOW_UP_SEQUENCE_TOLERANCE_MS;
};

const getMetadataObject = (displayMetadata: unknown): Record<string, unknown> | null => {
  return isMetadataObject(displayMetadata) ? displayMetadata : null;
};

const isMetadataObject = (displayMetadata: unknown): displayMetadata is Record<string, unknown> => {
  return Boolean(displayMetadata) && typeof displayMetadata === "object" && !Array.isArray(displayMetadata);
};

const getMetadataNumber = (metadata: Record<string, unknown> | null, keys: string[]): number | null => {
  if (!metadata) {
    return null;
  }

  for (const key of keys) {
    const value = metadata[key];

    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === "string") {
      const parsed = Number.parseInt(value, 10);

      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return null;
};

const getMetadataDate = (metadata: Record<string, unknown> | null, keys: string[]): Date | null => {
  if (!metadata) {
    return null;
  }

  for (const key of keys) {
    const value = metadata[key];

    if (typeof value !== "string") {
      continue;
    }

    const date = new Date(value);

    if (!Number.isNaN(date.getTime())) {
      return date;
    }
  }

  return null;
};

const formatHoursAgo = (date: Date): string => {
  const hours = Math.max(0, differenceInHours(new Date(), date));

  return `${hours} ${hours === 1 ? "hour" : "hours"}`;
};
